import { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { LinkPopover } from '../../chat/components/EditorToolbar';
import { LinkIcon } from '@heroicons/react/24/outline';
import { ToolbarButton } from './Toolbar';

interface LinkPopoverButtonProps {
    editor: Editor;
}

/**
 * Link toolbar button + popover. Reuses the chat LinkPopover so the
 * label-and-URL UX matches the chat composer. Drops the native window.prompt
 * fallback that used to live inline.
 */
export function LinkPopoverButton({ editor }: LinkPopoverButtonProps) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    const handleSubmit = (url: string, label: string): void => {
        const trimmedUrl = url.trim();
        // Same protocol whitelist the chat composer enforces.
        const urlObj = (() => {
            try {
                return new URL(trimmedUrl);
            } catch {
                return null;
            }
        })();
        if (urlObj && !['http:', 'https:', 'mailto:'].includes(urlObj.protocol)) return;

        const { from, to } = editor.state.selection;
        const hasSelection = from !== to;
        const selectedText = hasSelection ? editor.state.doc.textBetween(from, to) : '';
        const labelChanged = !!label && label !== selectedText;

        if (hasSelection && !labelChanged) {
            // Just apply (or update) the link mark on the existing selected text.
            editor.chain().focus().extendMarkRange('link').setLink({ href: trimmedUrl }).run();
        } else {
            // Either no selection (insert new) or selection with a different label
            // (replace). Build a text node that already carries the link mark so we
            // avoid the select-then-mark dance - the previous version's trailing
            // unsetMark stripped the link back off when a selection lingered.
            const text = label || (hasSelection ? selectedText : trimmedUrl);
            const linkNode = {
                type: 'text',
                text,
                marks: [{ type: 'link', attrs: { href: trimmedUrl } }]
            };
            const chain = editor.chain().focus();
            if (hasSelection) chain.deleteSelection();
            chain.insertContent(linkNode).run();
        }
        setOpen(false);
    };

    const selectedText = (() => {
        const { from, to } = editor.state.selection;
        return from !== to ? editor.state.doc.textBetween(from, to) : '';
    })();

    return (
        <div ref={wrapperRef} className="relative">
            <ToolbarButton
                title="Link"
                active={editor.isActive('link') || open}
                onClick={() => setOpen((o) => !o)}
            >
                <LinkIcon className="h-3.5 w-3.5" />
            </ToolbarButton>
            {open && (
                <div className="absolute top-full left-0 z-30 mt-1 w-[300px]">
                    <LinkPopover
                        initialUrl={editor.getAttributes('link').href ?? ''}
                        initialLabel={selectedText}
                        onSubmit={handleSubmit}
                        onRemove={() => {
                            editor.chain().focus().extendMarkRange('link').unsetLink().run();
                            setOpen(false);
                        }}
                        onClose={() => setOpen(false)}
                    />
                </div>
            )}
        </div>
    );
}
